'use client';

import { useEffect, useRef } from 'react';
import RadioGroup from '../../../atoms/RadioGroup/RadioGroup';
import { getValidationResponse } from '../utils';
import { useFormContext } from '../FormContext';
import type { RadioGroupField } from '../types';
import type {
  UseFormRegister,
  UseFormSetValue,
  UseFormWatch,
  UseFormTrigger,
  FieldValues,
  FieldErrors,
} from 'react-hook-form';

export type RadioGroupControlProps = {
  field: RadioGroupField;
  theme: 'dark' | 'light';
  direction: 'ltr' | 'rtl';
  register: UseFormRegister<FieldValues>;
  setValue: UseFormSetValue<FieldValues>;
  watch: UseFormWatch<FieldValues>;
  errors: FieldErrors<FieldValues>;
  trigger: UseFormTrigger<FieldValues>;
};

/**
 * Form control for radio group fields
 * @component
 * @param {RadioGroupControlProps} props - Component props
 * @returns {JSX.Element} RadioGroup control component
 */
const RadioGroupControl = ({
  field,
  theme,
  direction,
  register,
  setValue,
  watch,
  errors,
  trigger,
}: RadioGroupControlProps) => {
  const { disabled: formDisabled } = useFormContext();

  const depField = field.disabledDependency;
  const depValue = depField ? watch(depField.name) : undefined;
  const isDisabledByDependency = depField
    ? (() => {
        const match = depField.value !== undefined ? depValue === depField.value : !!depValue;
        return depField.negate ? !match : match;
      })()
    : false;

  const isDisabled = field.disabled || isDisabledByDependency || formDisabled;
  const currentValue = watch(field.name);

  // Auto-set the default value when the field appears without a form value
  // (e.g., conditionally rendered radio groups added after form initialization)
  const hasAutoSet = useRef(false);
  useEffect(() => {
    if (currentValue !== undefined || hasAutoSet.current) return;

    const defaultVal = field.defaultValue ?? field.options.find((option) => option.default)?.value;

    if (defaultVal !== undefined) {
      hasAutoSet.current = true;
      setValue(field.name, defaultVal, { shouldValidate: false });
    }
  }, [currentValue, field.defaultValue, field.name, field.options, setValue]);

  // Map options to include default based on current value
  const optionsWithDefault = field.options.map((option) => ({
    ...option,
    default: option.value === currentValue,
  }));

  return (
    <RadioGroup
      key={`form_field_${field.name}`}
      theme={theme}
      name={field.name}
      label={field.label}
      helperText={field.helperText}
      required={field.required}
      options={optionsWithDefault}
      selectedValue={currentValue}
      direction={direction}
      layout={field.layout}
      isDisabled={isDisabled}
      dataTestId={`radio-group-${field.name}`}
      gaComponent=""
      register={() =>
        register(field.name, {
          required: field.required ? field.validationMessages?.required : false,
        })
      }
      handleChange={(option) => setValue(field.name, option.value, { shouldValidate: true })}
      onBlur={() => trigger(field.name)}
      validationResponse={getValidationResponse({ field: field.name, errors })}
      badge={field.badge}
    />
  );
};

export default RadioGroupControl;
